import { useEffect, useRef, useState } from 'react';
import Draw from 'ol/interaction/Draw';
import Snap from 'ol/interaction/Snap';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import type { Map as OlMap } from 'ol';
import OlFeature from 'ol/Feature';
import { LineString } from 'ol/geom';
import GeoJSON from 'ol/format/GeoJSON';
import type { Tool } from '../contexts/@types/Map';
import { transform } from 'ol/proj';
import buffer from '@turf/buffer';
import difference from '@turf/difference';
import { featureCollection } from '@turf/helpers';
import { polygon as turfPolygon } from "@turf/helpers";
import lineSplit from '@turf/line-split';

export function useSplitFeatures(map: OlMap | undefined, selectedTool: Tool | null) {
  const [splitLayer, setSplitLayer] = useState<VectorLayer<any> | null>(null);

  const drawRef = useRef<Draw | null>(null);
  const snapRef = useRef<Snap | null>(null);

  const format = new GeoJSON();

  const handleSetSplitLayer = (id: string) => {
    if (!map) return;

    if (id === 'disable') {
      setSplitLayer(null);
      return;
    }

    const layer = map
      .getLayers()
      .getArray()
      .find((l: any) => {
        const props = l.getProperties?.() ?? {};
        return (props.layertitle === id || props.uuid === id) && l instanceof VectorLayer;
      }) as VectorLayer<any> | undefined;

    if (layer) setSplitLayer(layer);
  };

  const removeInteractions = () => {
    try {
      if (drawRef.current && map) map.removeInteraction(drawRef.current);
    } catch (e) { }
    try {
      if (snapRef.current && map) map.removeInteraction(snapRef.current);
    } catch (e) { }
    drawRef.current = null;
    snapRef.current = null;
    setSplitLayer(null);
  };

  function transformCoords(coords: any, from: string, to: string): any {
    if (typeof coords[0] === 'number') {
      return transform(coords, from, to);
    }
    return coords.map((c: any) => transformCoords(c, from, to));
  }

  function sanitizeLineCoords(coords: number[][]) {
    if (!coords || coords.length < 2) return null;

    const cleaned: number[][] = [];
    for (const coord of coords) {
      if (
        !Array.isArray(coord) ||
        coord.length < 2 ||
        !Number.isFinite(coord[0]) ||
        !Number.isFinite(coord[1])
      ) {
        continue;
      }

      const prev = cleaned[cleaned.length - 1];
      if (!prev || prev[0] !== coord[0] || prev[1] !== coord[1]) {
        cleaned.push([coord[0], coord[1]]);
      }
    }

    return cleaned.length >= 2 ? cleaned : null;
  }

  function splitPolygonWithLine(polygon: any, line: any) {
    const poly4326 = {
      ...polygon,
      geometry: {
        ...polygon.geometry,
        coordinates: transformCoords(
          polygon.geometry.coordinates,
          'EPSG:3857',
          'EPSG:4326'
        ),
      },
    };

    const line4326 = {
      ...line,
      geometry: {
        ...line.geometry,
        coordinates: transformCoords(
          line.geometry.coordinates,
          'EPSG:3857',
          'EPSG:4326'
        ),
      },
    };

    const buffered = buffer(line4326, 0.001, { units: 'kilometers' });

    const fc = featureCollection([poly4326, buffered]);

    const result = difference(fc as any);

    if (!result) return [];

    const to3857 = (geom: any) => ({
      ...geom,
      geometry: {
        ...geom.geometry,
        coordinates: transformCoords(
          geom.geometry.coordinates,
          'EPSG:4326',
          'EPSG:3857'
        ),
      },
    });

    if (result.geometry.type === 'MultiPolygon') {
      return result.geometry.coordinates.map((coords: any) =>
        to3857(turfPolygon(coords))
      );
    }

    return [to3857(result)];
  }

  function splitLineWithLine(line: any, splitter: any) {
    const coords3857 = line.geometry.coordinates;

    let densifiedCoords = coords3857;

    if (coords3857.length === 2) {
      densifiedCoords = densifyLine(coords3857, 4);
    }

    const line4326 = {
      ...line,
      geometry: {
        ...line.geometry,
        coordinates: transformCoords(
          densifiedCoords,
          'EPSG:3857',
          'EPSG:4326'
        ),
      },
    };

    const splitter4326 = {
      ...splitter,
      geometry: {
        ...splitter.geometry,
        coordinates: transformCoords(
          splitter.geometry.coordinates,
          'EPSG:3857',
          'EPSG:4326'
        ),
      },
    };

    const result = lineSplit(line4326, splitter4326);

    if (!result || !result.features.length) return [];

    return result.features
      .map((feat: any) => {
        const cleaned = sanitizeLineCoords(feat?.geometry?.coordinates);
        if (!cleaned) return null;

        const coords3857 = transformCoords(
          cleaned,
          'EPSG:4326',
          'EPSG:3857'
        );

        const finalCleaned = sanitizeLineCoords(coords3857);
        if (!finalCleaned) return null;

        return {
          ...feat,
          geometry: {
            type: 'LineString',
            coordinates: finalCleaned,
          },
        };
      })
      .filter(Boolean);
  }

  function densifyLine(coords: number[][], segments = 10) {
    const result: number[][] = [];

    for (let i = 0; i < coords.length - 1; i++) {
      const start = coords[i];
      const end = coords[i + 1];

      for (let j = 0; j <= segments; j++) {
        const t = j / segments;

        const x = start[0] + (end[0] - start[0]) * t;
        const y = start[1] + (end[1] - start[1]) * t;

        result.push([x, y]);
      }
    }

    return result;
  }

  function splitMultiLineWithLine(feature: any) {
    if (!feature?.geometry) return [];

    const { geometry, properties } = feature;

    if (geometry.type === 'LineString') {
      return [feature];
    }

    if (geometry.type === 'MultiLineString') {
      return geometry.coordinates.map((coords: number[][]) => ({
        type: 'Feature',
        properties: { ...properties },
        geometry: {
          type: 'LineString',
          coordinates: coords,
        },
      }));
    }

    return [];
  }

  function splitGeometryCollection(feature: any): any[] {
    if (!feature?.geometry) return [];

    const { geometry, properties } = feature;

    if (geometry.type === 'GeometryCollection') {
      return geometry.geometries.map((geom: any) => ({
        type: 'Feature',
        properties: { ...properties },
        geometry: geom,
      }));
    }

    return [feature];
  }

  useEffect(() => {
    if (!map) return;

    const isSplitTool = String(selectedTool?.action) === 'split';
    if (!isSplitTool || !splitLayer) {
      removeInteractions();
      return;
    }

    const source = splitLayer?.getSource?.() as VectorSource;

    if (!source) return;

    const draw = new Draw({ type: 'LineString', stopClick: true });
    drawRef.current = draw;

    if (source) {
      try {
        const snap = new Snap({ source });
        map.addInteraction(snap);
        snapRef.current = snap;
      } catch (e) { }
    }

    draw.on('drawend', (evt: any) => {
      const lineFeature: OlFeature = evt.feature;
      const lineGeom = lineFeature.getGeometry() as LineString | undefined;
      if (!lineGeom) return;

      try {
        const features = source.getFeatures();

        const toAdd: OlFeature[] = [];
        const toRemove: OlFeature[] = [];

        features.forEach((feat) => {
          const geom = feat.getGeometry();
          if (!geom) return;

          const originalStyle = feat.getStyle();
          const geoJSON = format.writeFeatureObject(feat);
          const lineGeoJSON = format.writeFeatureObject(lineFeature);

          let wasSplit = false;

          // GEOMETRYCOLLECTION
          if (geom.getType() === 'GeometryCollection') {
            const parts = splitGeometryCollection(geoJSON);

            toRemove.push(feat);

            parts.forEach((part: any) => {
              const partType = part.geometry.type;

              if (partType === 'Polygon' || partType === 'MultiPolygon') {
                const result = splitPolygonWithLine(part, lineGeoJSON);

                if (result && result.length > 0) {
                  result.forEach((item: any) => {
                    const newFeature = format.readFeature(item);

                    const props = { ...feat.getProperties() };
                    delete props.geometry;
                    newFeature.setProperties(props);

                    if (originalStyle) newFeature.setStyle(originalStyle);

                    toAdd.push(newFeature);
                  });
                }
              }

              if (partType === 'LineString' || partType === 'MultiLineString') {
                const lines = splitMultiLineWithLine(part);

                lines.forEach((linePart: any) => {
                  const result = splitLineWithLine(linePart, lineGeoJSON);

                  if (result && result.length > 0) {
                    result.forEach((item: any) => {
                      const newFeature = format.readFeature(item);

                      const props = { ...feat.getProperties() };
                      delete props.geometry;
                      newFeature.setProperties(props);

                      if (originalStyle) newFeature.setStyle(originalStyle);

                      toAdd.push(newFeature);
                    });
                  }
                });
              }
            });

            return;
          }

          //POLYGON / MULTIPOLYGON
          if (geom.getType() === 'Polygon' || geom.getType() === 'MultiPolygon') {
            const result = splitPolygonWithLine(geoJSON, lineGeoJSON);

            if (result && result.length > 1) {
              result.forEach((item) => {
                const newFeature = format.readFeature(item);

                const props = { ...feat.getProperties() };
                delete props.geometry;
                newFeature.setProperties(props);

                if (originalStyle) newFeature.setStyle(originalStyle);

                toAdd.push(newFeature);
              });

              toRemove.push(feat);
            }

            return;
          }

          //LINE / MULTILINE
          if (geom.getType() === 'LineString' || geom.getType() === 'MultiLineString') {

            const lines = splitMultiLineWithLine(geoJSON);

            lines.forEach((linePart: any) => {
              const result = splitLineWithLine(linePart, lineGeoJSON);

              if (result && result.length > 1) {
                wasSplit = true;

                result.forEach((item: any) => {
                  const newFeature = format.readFeature(item);

                  const props = { ...feat.getProperties() };
                  delete props.geometry;
                  newFeature.setProperties(props);

                  if (originalStyle) newFeature.setStyle(originalStyle);

                  toAdd.push(newFeature);
                });
              } else {

                const originalPart = format.readFeature(linePart);

                const props = { ...feat.getProperties() };
                delete props.geometry;
                originalPart.setProperties(props);

                if (originalStyle) originalPart.setStyle(originalStyle);

                toAdd.push(originalPart);
              }
            });

            if (wasSplit) {
              toRemove.push(feat);
            }

            return;
          }
        });

        toRemove.forEach(f => source.removeFeature(f));
        toAdd.forEach(f => source.addFeature(f));

      } catch (err) {
        console.error('[useSplitFeatures] erro no split:', err);
      }
    });

    map.addInteraction(draw);

    return () => {
      removeInteractions();
    };
  }, [map, splitLayer, selectedTool?.action]);

  useEffect(() => {
    return () => removeInteractions();
  }, []);

  return {
    splitLayer,
    handleSetSplitLayer,
  };
}